import React, {
  useEffect, useState, useMemo, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import vlog from '../../javascript/vlog';
import ContactRow from './contact_row';
import EmailContacts from '../dashboard/recently_messaged_users/email_contacts';
import Search from '../dashboard/recently_messaged_users/search';

const PER_PAGE = 10;
const BATCH_SIZE = 30;

const PROFILE_TYPE_LABELS = {
  type_promoter_pr: 'PR',
  type_promoter_expert: 'Expert',
  type_promoter_hybrid: 'Expert & PR',
  type_media: 'Media',
};

function humanizeProfileType(typeOfProfile) {
  if (!typeOfProfile) return '';
  return PROFILE_TYPE_LABELS[typeOfProfile]
    || typeOfProfile.replace(/^type_/, '').replace(/_/g, ' ');
}

async function fetchHashtags(profileIds) {
  if (!profileIds.length) return {};

  const { data: tags, error } = await window.supabaseClient
    .from('derived_tags')
    .select('profile_id, hashtag_name, hashtag_slug')
    .in('profile_id', profileIds)
    .eq('is_added', true);

  if (error) {
    vlog.error('Error fetching hashtags:', error);
    return {};
  }

  const grouped = {};
  for (const tag of (tags || [])) {
    if (!grouped[tag.profile_id]) grouped[tag.profile_id] = [];
    if (!grouped[tag.profile_id].some((h) => h.slug === tag.hashtag_slug)) {
      grouped[tag.profile_id].push({
        name: tag.hashtag_name,
        slug: tag.hashtag_slug,
      });
    }
  }
  return grouped;
}

async function fetchV1ProfileLinks(profileIds) {
  if (!profileIds.length) return {};

  const { data: profiles, error } = await window.supabaseClient
    .from('user_profiles')
    .select('profile_id, profile_migrate_id, profile_migrate_type')
    .in('profile_id', profileIds);

  if (error) {
    vlog.error('Error fetching profile links:', error);
    return {};
  }

  const linkMap = {};
  const prUserProfileIds = [];
  const prUserMigrateIds = {};

  for (const p of (profiles || [])) {
    if (p.profile_migrate_type === 'Reporter') {
      linkMap[p.profile_id] = `/reporters/${p.profile_migrate_id}`;
    } else if (p.profile_migrate_type === 'Source') {
      linkMap[p.profile_id] = `/sources/${p.profile_migrate_id}`;
    } else if (p.profile_migrate_type === 'PrUser') {
      prUserProfileIds.push(p.profile_id);
      prUserMigrateIds[p.profile_id] = p.profile_migrate_id;
    }
  }

  // PrUsers with self-expert link to /sources/<source_id>,
  // otherwise link to /pr_users/<pr_user_migrate_id>
  if (prUserProfileIds.length > 0) {
    const { data: reps } = await window.supabaseClient
      .from('representations')
      .select('promoter_profile_id, promoted_entity_id')
      .in('promoter_profile_id', prUserProfileIds)
      .eq('self_expert', true);

    if (reps && reps.length > 0) {
      const entityIds = reps.map((r) => r.promoted_entity_id);

      const { data: entities } = await window.supabaseClient
        .from('promo_entities')
        .select('id, migrate_id')
        .in('id', entityIds);

      const entityMigrateMap = {};
      for (const e of (entities || [])) {
        entityMigrateMap[e.id] = e.migrate_id;
      }

      for (const rep of reps) {
        const sourceId = entityMigrateMap[rep.promoted_entity_id];
        if (sourceId) {
          linkMap[rep.promoter_profile_id] = `/sources/${sourceId}`;
        }
      }
    }

    // Remaining PrUsers without self-expert Source → /pr_users/<migrate_id>
    for (const profileId of prUserProfileIds) {
      if (!linkMap[profileId] && prUserMigrateIds[profileId]) {
        linkMap[profileId] = `/pr_users/${prUserMigrateIds[profileId]}`;
      }
    }
  }

  return linkMap;
}

async function fetchPitchCounts(promoterProfileIds, reporterProfileId) {
  if (!promoterProfileIds.length) return {};

  const pitchCountPromises = promoterProfileIds.map(async (promoterId) => {
    const { count, error } = await window.supabaseClient
      .from('pitches')
      .select('*', { count: 'exact', head: true })
      .eq('profile_id', promoterId)
      .eq('request_media_person_id', reporterProfileId)
      .not('sent_at', 'is', null);

    return { promoterId, count: error ? 0 : (count || 0) };
  });

  const results = await Promise.all(pitchCountPromises);
  const counts = {};
  for (const { promoterId, count } of results) {
    counts[promoterId] = count;
  }
  return counts;
}

function filterContacts(contacts, hashtagsByProfile, term) {
  if (!term || !term.trim()) return contacts;
  const lower = term.toLowerCase();

  return contacts.filter((contact) => {
    const nameMatch = contact.name.toLowerCase().includes(lower);
    const employmentMatch = contact.titleAtWorkplace?.toLowerCase().includes(lower)
      || contact.workplaceCompanyName?.toLowerCase().includes(lower);
    const hashtagMatch = (hashtagsByProfile[contact.profileId] || [])
      .some((h) => h.name.toLowerCase().includes(lower));
    return nameMatch || employmentMatch || hashtagMatch;
  });
}

function generateCsv(contacts, hashtagsByProfile, selectedIds) {
  const selected = contacts.filter((c) => selectedIds.has(c.profileId));
  if (selected.length === 0) return;

  const csvRows = [['Name', 'Type', 'Expert On'].join(',')];
  selected.forEach((contact) => {
    const hashtags = (hashtagsByProfile[contact.profileId] || [])
      .map((h) => h.name).join(', ');
    csvRows.push([
      `"${(contact.name || '').replace(/"/g, '""')}"`,
      `"${(contact.typeOfProfile || '').replace(/"/g, '""')}"`,
      `"${(hashtags || '').replace(/"/g, '""')}"`,
    ].join(','));
  });

  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `my-contacts-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function ContactsTable({ v2ProfileId }) {
  const [contacts, setContacts] = useState([]);
  const [hashtagsByProfile, setHashtagsByProfile] = useState({});
  const [pitchCountsByProfile, setPitchCountsByProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const seenSenderIds = useRef(new Set());
  const fetchOffset = useRef(0);

  const fetchBatch = useCallback(async (isInitial) => {
    if (isInitial) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const from = fetchOffset.current;
      const to = from + BATCH_SIZE - 1;

      const { data: conversations, error: convError } = await window.supabaseClient
        .from('conversations')
        .select(`
          migrate_id,
          last_sent_at,
          num_messages,
          conversation_senders
        `)
        .eq('receiver_profile_id', v2ProfileId)
        .order('last_sent_at', { ascending: false })
        .range(from, to);

      if (convError) {
        vlog.error('Error fetching conversations:', convError);
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      const rows = conversations || [];
      fetchOffset.current += rows.length;

      if (rows.length < BATCH_SIZE) {
        setHasMore(false);
      }

      const currentUserSlug = window.CURRENT_USER_JSON?.attributes?.slug;

      // Expand each conversation into one row per non-current-user sender,
      // deduplicated across batches via seenSenderIds.
      const newContacts = [];
      for (const conv of rows) {
        const senders = (conv.conversation_senders || [])
          .filter((sender) => sender.id !== v2ProfileId && !seenSenderIds.current.has(sender.id));

        for (const sender of senders) {
          seenSenderIds.current.add(sender.id);
          newContacts.push({
            profileId: sender.id,
            name: sender.name || 'Unknown User',
            avatarUrl: sender.profile_image_url || null,
            titleAtWorkplace: sender.title_at_workplace || '',
            workplaceCompanyName: sender.workplace_company_name || '',
            rawTypeOfProfile: sender.type_of_profile || '',
            typeOfProfile: humanizeProfileType(sender.type_of_profile),
            numMessages: conv.num_messages || 0,
            conversationId: conv.migrate_id,
            conversationPath: currentUserSlug
              ? `/users/${currentUserSlug}/conversations/${conv.migrate_id}`
              : null,
          });
        }
      }

      // Enrich only the new contacts from this batch
      const newProfileIds = newContacts.map((c) => c.profileId);

      const [newHashtags, newPitchCounts, newProfileLinks] = await Promise.all([
        fetchHashtags(newProfileIds),
        fetchPitchCounts(newProfileIds, v2ProfileId),
        fetchV1ProfileLinks(newProfileIds),
      ]);

      const enrichedNew = newContacts.map((c) => ({
        ...c,
        v1ProfilePath: newProfileLinks[c.profileId] || null,
      }));

      setContacts((prev) => [...prev, ...enrichedNew]);
      setHashtagsByProfile((prev) => ({ ...prev, ...newHashtags }));
      setPitchCountsByProfile((prev) => ({ ...prev, ...newPitchCounts }));
    } catch (err) {
      vlog.error('Unexpected error fetching contacts:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [v2ProfileId]);

  useEffect(() => {
    fetchBatch(true);
  }, [fetchBatch]);

  const filteredContacts = useMemo(
    () => filterContacts(contacts, hashtagsByProfile, searchTerm),
    [contacts, hashtagsByProfile, searchTerm],
  );

  const totalPages = Math.ceil(filteredContacts.length / PER_PAGE);
  const paginatedContacts = filteredContacts.slice(
    (currentPage - 1) * PER_PAGE,
    currentPage * PER_PAGE,
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const allSelected = paginatedContacts.length > 0
    && paginatedContacts.every((c) => selectedIds.has(c.profileId));

  const handleSelectAll = () => {
    const newSelected = new Set(selectedIds);
    if (allSelected) {
      paginatedContacts.forEach((c) => newSelected.delete(c.profileId));
    } else {
      paginatedContacts.forEach((c) => newSelected.add(c.profileId));
    }
    setSelectedIds(newSelected);
  };

  const handleSelectOne = (profileId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(profileId)) {
      newSelected.delete(profileId);
    } else {
      newSelected.add(profileId);
    }
    setSelectedIds(newSelected);
  };

  const handleDownloadCsv = (e) => {
    e.preventDefault();
    generateCsv(contacts, hashtagsByProfile, selectedIds);
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-secondary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-0">
      <div className="d-flex gap-2 align-items-stretch mb-3 flex-column flex-md-row">
        <div className="flex-grow-1 my-contacts-search-wrapper">
          <Search
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
          />
        </div>
        <EmailContacts
          button_text="Invite Contacts"
          button_class="btn btn-primary d-flex align-items-center gap-2 text-nowrap my-contacts-invite-btn"
          button_icon="fa-regular fa-envelope"
        />
      </div>

      <div className="bg-white border rounded-3 shadow-sm overflow-hidden">
        <table className="table table-hover align-middle mb-0 my-contacts-table">
          <thead>
            <tr className="border-bottom">
              <th className="bg-light py-3 ps-3">
                <div className="d-flex align-items-center">
                  <input
                    type="checkbox"
                    className="form-check-input mt-0 my-contacts-checkbox"
                    checked={allSelected}
                    onChange={handleSelectAll}
                  />
                  <span className="ms-2 small text-uppercase fw-semibold text-muted">
                    Select All
                  </span>
                </div>
              </th>
              <th className="bg-light py-3 small text-uppercase fw-semibold text-muted">Type</th>
              <th className="bg-light py-3 small text-uppercase fw-semibold text-muted d-none d-md-table-cell">
                Expert on
              </th>
              <th className="bg-light py-3 small text-uppercase fw-semibold text-muted text-end">
                Pitches
              </th>
              <th className="bg-light py-3 small text-uppercase fw-semibold text-muted text-end">
                Messages
              </th>
              <th className="bg-light py-3 pe-3 text-end">
                <button
                  type="button"
                  className="btn btn-link btn-sm text-decoration-none p-0"
                  onClick={handleDownloadCsv}
                  disabled={selectedIds.size === 0}
                >
                  <i className="fa-solid fa-download me-1" />
                  Download CSV
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedContacts.map((contact) => (
              <ContactRow
                key={contact.profileId}
                contact={contact}
                hashtags={hashtagsByProfile[contact.profileId] || []}
                pitchCount={pitchCountsByProfile[contact.profileId] || 0}
                selected={selectedIds.has(contact.profileId)}
                onSelect={() => handleSelectOne(contact.profileId)}
              />
            ))}
          </tbody>
        </table>

        {filteredContacts.length === 0 && searchTerm && (
          <div className="text-center text-muted py-5">
            <i className="fa-regular fa-face-meh fs-1 mb-3 d-block" />
            <p className="mb-0">
              No contacts found matching &quot;
              {searchTerm}
              &quot;
            </p>
          </div>
        )}

        {filteredContacts.length === 0 && !searchTerm && (
          <div className="text-center text-muted py-5">
            <i className="fa-regular fa-address-book fs-1 mb-3 d-block" />
            <p className="mb-1">No contacts yet</p>
            <small>Start a conversation to see contacts here.</small>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <nav aria-label="Contacts pagination" className="mt-3">
          <ul className="pagination pagination-sm justify-content-center mb-0">
            <li className={`page-item${currentPage === 1 ? ' disabled' : ''}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                &laquo;
              </button>
            </li>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <li key={page} className={`page-item${currentPage === page ? ' active' : ''}`}>
                <button
                  type="button"
                  className="page-link"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              </li>
            ))}
            <li className={`page-item${currentPage === totalPages ? ' disabled' : ''}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                &raquo;
              </button>
            </li>
          </ul>
        </nav>
      )}

      {hasMore && !loading && (
        <div className="text-center mt-3">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => fetchBatch(false)}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading\u2026' : 'Load More Contacts'}
          </button>
        </div>
      )}
    </div>
  );
}

ContactsTable.propTypes = {
  v2ProfileId: PropTypes.number.isRequired,
};

export default ContactsTable;
